import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { Divider, MenuItem } from '@mui/material';
import { ChevronDown, ChevronUp, Plus } from 'lucide-react';
import { Dot } from 'lucide-react';

import {
  type TypeScenario,
  type TypeScenarioListDisplay,
} from '../../common/dataTypes';
import {
  useActivateBaseScenario,
  useActivateScenario,
} from '../../hooks/tanstack/useScenariosHook';
import {
  useGetDistinguishModel,
  useGetRouteParams,
} from '../../hooks/tanstack/useUtilsHook';
import useModelStore from '../../hooks/zustand/useModelStore';
import MenuDropdown from '../MenuDropdown';
import WaitingModelOutputDialog from '../WaitingModelOutputDialog';

import CreateScenarioFormDialog from './CreateScenarioFormDialog';

type ScenarioMenuDropdownProps = {
  dataTableName: string;
  dataTableDisplayName: string;
  isPivot: boolean;
  defaultValue: {
    id: number;
    label: string;
  };
  scenarios: TypeScenarioListDisplay['groups'][0]['data_table_schemas'][0]['scenarios'];
  handleSetIsLoading?: (loading: boolean) => void;
};

const ScenarioMenuDropdown = ({
  dataTableName,
  dataTableDisplayName,
  defaultValue,
  isPivot,
  handleSetIsLoading,
  scenarios,
}: ScenarioMenuDropdownProps) => {
  const navigate = useNavigate();

  const [openCreateScenarioDialog, setOpenCreateScenarioDialog] =
    useState(false);

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const { orgId, projectId, modelId } = useGetRouteParams();

  const { isRunningModelOutputServer } = useGetDistinguishModel();

  const [openWaitingModelOutput, setOpenWaitingModelOutput] = useState(false);

  const { mutateAsync: activateScenario } = useActivateScenario({
    onSuccess: () => {
      handleSetIsLoading?.(false);
    },
  });

  const { mutateAsync: activateBaseScenario } = useActivateBaseScenario({
    onSuccess: () => {
      handleSetIsLoading?.(false);
    },
  });

  const updateRunningModelOutput = useModelStore(
    (state) => state.updateIsRunningModelOutput
  );

  const handleOpenCreateScenarioDialog = () => {
    setOpenCreateScenarioDialog(true);
  };

  const handleCloseCreateScenarioDialog = () => {
    setOpenCreateScenarioDialog(false);
  };

  const handleSubmitCreateScenarioDialog = (data: TypeScenario) => {
    navigate(`input-table/${dataTableName}?scenario_id=${data.id}`);
  };

  const handleActivateScenario = async (id: number) => {
    if (id === defaultValue.id) {
      setAnchorEl?.(null);
      return;
    }

    handleSetIsLoading?.(true);

    updateRunningModelOutput(null);

    if (id === -1) {
      setAnchorEl?.(null);
      return;
    }

    if (id === 0) {
      await activateBaseScenario({
        orgId,
        projectId,
        modelId,
        dataTableName,
      });
      return;
    }

    await activateScenario({
      orgId,
      projectId,
      modelId,
      scenarioId: id,
      dataTableName,
    });
  };

  return (
    <>
      <MenuDropdown
        anchorEl={anchorEl}
        setAnchorEl={setAnchorEl}
        open={open}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        trigger={
          <div className="flex justify-between w-full gap-2">
            <p className="flex text-start items-center whitespace-normal">
              {scenarios.length > 1 ? <Dot size={30} /> : null}{' '}
              {defaultValue.label}
            </p>
            {open ? <ChevronUp width={15} /> : <ChevronDown width={15} />}
          </div>
        }
        style={{
          width: '100%',
          border: 'none !important',
          top: '10px !important',
        }}
        triggerClassName="rounded-md w-full"
      >
        <MenuItem
          onClick={() => {
            isRunningModelOutputServer
              ? setOpenWaitingModelOutput(true)
              : handleOpenCreateScenarioDialog();
          }}
        >
          <div className="py-1 gap-2 font-semibold flex items-center ">
            <Plus />
            <p>New Scenario</p>
          </div>
        </MenuItem>
        <Divider
          style={{
            margin: 0,
          }}
        />
        {scenarios?.map((s) => (
          <MenuItem
            onClick={() =>
              isRunningModelOutputServer
                ? setOpenWaitingModelOutput(true)
                : handleActivateScenario(s.id)
            }
            className="flex transition hover:bg-slate-200 p-2 cursor-pointer w-full h-full gap-2"
          >
            {s.name}
          </MenuItem>
        ))}
      </MenuDropdown>
      <CreateScenarioFormDialog
        open={openCreateScenarioDialog}
        title="Create A New Scenario"
        showTrigger={false}
        handleCloseDialog={handleCloseCreateScenarioDialog}
        handleSubmitDialog={handleSubmitCreateScenarioDialog}
        dataTableName={dataTableName}
        dataTableDisplayName={dataTableDisplayName}
      />
      <WaitingModelOutputDialog
        open={openWaitingModelOutput}
        handleClose={() => {
          setOpenWaitingModelOutput(false);
        }}
      />
    </>
  );
};

export default ScenarioMenuDropdown;
